import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';

import { authorizationHeader } from '@picsart/growth-rc/services/auth';

import useSingleViewMeta from 'templates/SingleView/hooks/useSingeViewMeta';
import type { SingleViewProps } from 'templates/SingleView/types';

import getCategoryMediaUrlPrefix from 'utils/getCategoryMediaUrlPrefix';
import endpointConstructor from 'utils/shared/endpointConstructor';
import { setImageInfo } from 'store/actions/imageInfo';
import { IMAGE_ROUTE_PREFIXES } from 'tmp/main';
import fetcher from 'utils/shared/fetcher';
import { store } from 'store';

import type { SSRContext } from 'types/SSRContext';

const SingleView = dynamic(() => import('templates/SingleView'));
const Layout = dynamic(() => import('components/Layout'));

const imagePathRegex = /(^\d+$)|(^(image|sticker|gif)-\d+$)/;

export const getServerSideProps: GetServerSideProps<SingleViewProps> = async ({
  query: { id },
  req,
}: SSRContext) => {
  const imageId = id as string;

  // image url pattern should be only digits or (image|sticker|gif)-digits
  if (!imageId || !imagePathRegex.test(imageId as string)) {
    return {
      notFound: true,
    };
  }
  const _accessToken = req?.tokenData?.access_token || '';

  const getServerSideCookie = (await import('utils/shared/getCookie')).default;
  const deviceId = getServerSideCookie(req.headers.cookie, 'paa-did');

  const imageData = await fetcher(endpointConstructor('i', `${imageId}`), {
    headers: { deviceId, ...authorizationHeader(_accessToken) },
  });

  if (!imageData?.imageData) {
    return {
      notFound: true,
    };
  }

  const { type, url } = imageData.imageData;
  const imageTypePrefix = getCategoryMediaUrlPrefix(type, url);
  const pathPrefix = imageId.split('-')[0];

  // return 404 if image type doesn't correspond to url image path prefix
  if (IMAGE_ROUTE_PREFIXES.includes(pathPrefix) && imageTypePrefix !== pathPrefix) {
    return {
      notFound: true,
    };
  }

  store.publish(setImageInfo(imageData));

  return {
    props: {
      ...imageData,
      accessToken: _accessToken,
    },
  };
};

function ImageDefault({
  isMobile,
  isTablet,
  imageData,
  imagesIds,
  isWebView,
  domain,
  accessToken,
}: SingleViewProps) {
  const convertData = () => {
    const {
      id,
      url,
      title,
      type,
      tags,
      license,
      width,
      height,
      views_count,
      likes_count,
      show_edit_history,
      created,
      user,
      source_info,
    } = imageData;

    return {
      id,
      url,
      title,
      type,
      tags,
      user,
      license,
      width,
      height,
      created,
      views_count,
      likes_count,
      source_info,
      show_edit_history,
      accessToken,
    };
  };

  const { metaTitle, metaDescription, canonicalUrl } = useSingleViewMeta({
    data: convertData(),
    domain,
  });

  return (
    <Layout
      title={metaTitle}
      description={metaDescription}
      canonicalUrl={canonicalUrl}
      isMobile={isMobile}
      isWebView={isWebView}
      ogImage={imageData.url}
      {...((metaTitle || metaDescription) && {
        ogTitle: metaTitle,
        ogDescription: metaDescription,
      })}
      ogType="picsartphotostudio:photo"
      alternateUrl={`/i/${imageData.id}`}
      imageMetas={{
        username: `@${imageData.user.username}`,
        width: String(imageData.width),
        height: String(imageData.height),
      }}
      // All set pages should be marked as no index as per SEO request
      noIndex
    >
      <SingleView
        isMobile={isMobile}
        imageData={imageData}
        domain={domain}
        isTablet={isTablet}
        imagesIds={imagesIds}
        accessToken={accessToken}
      />
    </Layout>
  );
}

export default ImageDefault;
